import React, { useState, useEffect } from 'react';
import DEPLOYMENT_CONFIG from '../../config/deployment';
import './WonderTradeLoader.css';

interface WonderTradeLoaderProps {
  /**
   * Size of the loader in pixels. Default: 120
   */
  size?: number;
  
  /**
   * Additional CSS class name
   */
  className?: string;
  
  /**
   * Whether to show as full-screen overlay. Default: false
   */
  fullScreen?: boolean;
  
  /**
   * Whether to show white background. Default: true
   */
  whiteBackground?: boolean;
  
  /**
   * Custom message to display below the loader
   */
  message?: string;
}

/**
 * WonderTradeLoader - A reusable loading component with animated logo
 * 
 * @example
 * // Basic usage
 * <WonderTradeLoader />
 * 
 * @example
 * // Full screen loading
 * <WonderTradeLoader fullScreen={true} />
 * 
 * @example
 * // Custom size with message
 * <WonderTradeLoader size={150} message="Loading..." />
 */
const WonderTradeLoader: React.FC<WonderTradeLoaderProps> = ({
  size = 120,
  className = '',
  fullScreen = false,
  whiteBackground = true,
  message
}) => {
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  // Fetch logo from API on component mount
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const token = localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
        const headers: HeadersInit = {
          'Content-Type': 'application/json'
        };
        
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
        const response = await fetch(`${backendUrl}/api/settings/logo`, {
          method: 'GET',
          headers
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.logo) {
            setLogoUrl(data.logo);
          }
          // If logo doesn't exist (success: false), loader will still show without logo
        }
      } catch (error) {
        // Network errors - still show loader without logo
      }
    };

    fetchLogo();
  }, []);

  // Show loader immediately (with or without logo)
  // The spinning border will always be visible

  // Logo size: leave space for spinner border, keep logo ~70% of inner area
  const logoSize = Math.max(16, (size - 16) * 0.7);
  
  // Build inline styles for the logo background - only if logo exists
  const logoBackgroundStyle: React.CSSProperties | undefined = logoUrl ? {
    backgroundImage: `url(${logoUrl})`,
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    width: logoSize,
    height: logoSize,
    borderRadius: '50%',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 2,
  } : undefined;
  
  const loaderContent = (
    <div className={`wondertrade-loader-container ${className}`} style={{ width: size, height: size }}>
      <div className="wondertrade-loader-border"></div>
      {logoUrl && logoBackgroundStyle && (
        <div 
          className="wondertrade-loader-logo"
          style={logoBackgroundStyle}
        />
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div 
        className={`wondertrade-loader-fullscreen ${whiteBackground ? 'wondertrade-loader-white-bg' : ''}`}
      >
        {loaderContent}
        {message && (
          <div className="wondertrade-loader-message">{message}</div>
        )}
      </div>
    );
  }

  return (
    <div className="wondertrade-loader-inline">
      {loaderContent}
      {message && (
        <div className="wondertrade-loader-message">{message}</div>
      )}
    </div>
  );
};

export default WonderTradeLoader;

